// src/Pages/Dashboard.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLogout } from "../Hooks/useLogout";
import "../Styles/Dashboard.css";
import axios from "axios";
import { useAuthContext } from "../Hooks/useauthContext";
import { API_URL } from "../config";
import Navbar from "../Components/Navbar";
import Spinner from "../Components/Spinner";
import { toast } from "react-toastify";
import { lightBlue } from "@mui/material/colors";
import { getUserId } from "../Context/auth";

const Dashboard = () => {
  const [email, setEmail] = useState("");
  const [totalSummary, setTotalSummary] = useState(0);
  const [totalHazardAnalysis, setTotalHazardAnalysis] = useState(0);
  const [totalSystemSpecifications, setTotalSystemSpecifications] = useState(0);
  const [totalHuddlerooms, setTotalHuddlerooms] = useState(0);
  const [totalAudio, setTotalAudio] = useState("");
  const [totalDiagrams, setTotalDiagrams] = useState(0);
  const { logout } = useLogout();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const token = user.token;
  const [userPlan, setUserPlan] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false); // check if user info is fetched successfully
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // States for expandable tabs
  const [activeTab, setActiveTab] = useState("huddleroom");

  // Load email from local storage on mount
  // useEffect(() => {
  //   const localUser = JSON.parse(localStorage.getItem("user"));
  //   if (localUser) {
  //     setEmail(localUser.name);
  //     setIsLoggedIn(true);
  //   }
  // }, []);

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
  };

  const sendVerificationEmail = async (email) => {
    try {
      const response = await axios.get(`${API_URL}/send_verification`, {
        params: { user_email: email },
      });
      console.log(response.data.message);
      return response.data;
    } catch (error) {
      console.error(
        "Error sending verification email:",
        error.response?.data?.detail || error.message
      );
      throw error;
    }
  };

  // Fetch additional user info for Huddleroom statistics
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setIsLoading(true);
        let userData;

        // Retrieve the 'user' cookie and parse the stored JSON
        const userCookie = getCookie("user");
        if (userCookie) {
          userData = JSON.parse(decodeURIComponent(userCookie));
        } else {
          // If not found in cookies, try local storage.
          const storedUser = localStorage.getItem("user");
          if (storedUser) {
            userData = JSON.parse(storedUser);
          }
        }
        // If no user data is found in either, log out.
        if (!userData) {
          logout();
          return;
        }

        // Get the user id from the token
        const userId = getUserId();
        const token = userData.token;

        const response = await axios.get(`${API_URL}/user_info`, {
          params: {
            user_id: userId,
            token: token,
          },
        });

        setEmail(response.data.name);
        setIsLoggedIn(true);

        setTotalSummary(response.data.huddleroom.total_summary_generations);
        setTotalHazardAnalysis(
          response.data.huddleroom.total_hazard_analysis_generations
        );
        setTotalSystemSpecifications(
          response.data.huddleroom.total_system_specification_generations
        );
        setTotalHuddlerooms(response.data.huddleroom.total_huddlerooms_created);
        setTotalDiagrams(response.data.robosysml.total_diagram_generations);

        const seconds = response.data.huddleroom.total_audio_seconds;
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours <= 0) {
          setTotalAudio(`${minutes}m`);
        } else {
          setTotalAudio(`${hours}hrs ${minutes}m`);
        }

        setUserPlan(response.data.plan);
        setIsVerified(response.data.is_verified);
        console.log(response.data.is_verified);
        setIsLoading(false);

        // Mark that we have successfully fetched user info so that
        // subsequent renders do not trigger fetchUserInfo again.
        setHasFetched(true);
      } catch (error) {
        console.error("Error fetching user info:", error);
        logout();
      }
    };

    if (!hasFetched) {
      fetchUserInfo();
    }
  }, [user, logout]);

  const handleLogout = () => {
    logout();
    navigate("/home");
  };

  const handlePremium = () => {
    navigate("/pricing");
  };

  const handleHuddleRoomClick = () => {
    if (!isVerified) {
      toast.error("Please verify your email");
    } else {
      window.location.href = "https://huddleroom.robofication.com";
    }
  };

  const handleSysMLClick = () => {
    if (!isVerified) {
      toast.error("Please verify your email");
    } else {
      window.location.href = "https://sysml.robofication.com";
    }
  };

  const handleVerifyClick = async () => {
    try {
      const response = await sendVerificationEmail(user.email);
      // Optionally, show a toast or alert
      alert(response.message || "Verification email sent");
    } catch (error) {
      alert("Error sending verification email");
    }
  };

  useEffect(() => {
    if (!isVerified) {
      // Add a delay of 2 seconds before showing the modal
      const timer = setTimeout(() => {
        setShowVerificationModal(true);
      }, 3000); // 2000 milliseconds = 2 seconds

      // Cleanup the timer when component unmounts
      return () => clearTimeout(timer);
    }
  }, [isVerified]);

  return (
    <div className="dashboard-page">
      {/* Place the Navbar outside the dashboard container */}
      <Navbar />
      <div className="dashboard-container">
        {/* Left Container: Stacks Summary (Top) and Tabs/Link Grid (Bottom) */}
        <div className="dashboard-left-container">
          <div className="dashboard-left">
            <div className="summary-card">
              <h2>Overview</h2>
              <section className="stats-section">
                <div className="stats-container">
                  <div className="stats-card">
                    <div className="stats-icon">
                      <p className="stats-number">{totalSummary}</p>
                    </div>
                    <div className="stats-description">
                      <p>Total Summaries</p>
                    </div>
                  </div>
                  <div className="stats-card">
                    <div className="stats-icon">
                      <p className="stats-number">{totalAudio}</p>
                    </div>
                    <div className="stats-description">
                      <p>Total Audio Processed</p>
                    </div>
                  </div>
                  <div className="stats-card">
                    <div className="stats-icon">
                      <p className="stats-number">{totalHazardAnalysis}</p>
                    </div>
                    <div className="stats-description">
                      <p>Total Hazard Analyzes</p>
                    </div>
                  </div>
                  <div className="stats-card">
                    <div className="stats-icon">
                      <p className="stats-number">
                        {totalSystemSpecifications}
                      </p>
                    </div>
                    <div className="stats-description">
                      <p>Total System Specifications</p>
                    </div>
                  </div>
                  <div className="stats-card">
                    <div className="stats-icon">
                      <p className="stats-number">{totalDiagrams}</p>
                    </div>
                    <div className="stats-description">
                      <p>Total Diagram Generations</p>
                    </div>
                  </div>
                  <div className="stats-card">
                    <div className="stats-icon">
                      <p className="stats-number"></p>
                    </div>
                    <div className="stats-description">
                      <p>Coming Soon</p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
          <div className="divider"></div>
          <div className="dashboard-center">
            <h1>Tools Overview</h1>
            <div className="content-area">
              {/* Tabs Section */}
              <div className="tabs-wrapper">
                {/* Horizontal Tabs */}
                <div className="tabs">
                  <button
                    className={activeTab === "huddleroom" ? "active" : ""}
                    onClick={() => setActiveTab("huddleroom")}
                  >
                    Huddleroom
                  </button>
                  <button
                    className={activeTab === "robosysml" ? "active" : ""}
                    onClick={() => setActiveTab("robosysml")}
                  >
                    RoboSysML
                  </button>
                </div>

                {/* Content for the active tab */}
                <div key={activeTab} className="tab-content">
                  {activeTab === "huddleroom" && (
                    <div className="huddleroom-content">
                      <div className="stat-item">
                        <h3>Total Summaries</h3>
                        <p>{totalSummary}</p>
                      </div>
                      <div className="stat-item">
                        <h3>Total Systems Descriptions</h3>
                        <p>{totalSystemSpecifications}</p>
                      </div>
                      <div className="stat-item">
                        <h3>Total Hazard Analyzes</h3>
                        <p>{totalHazardAnalysis}</p>
                      </div>
                      <div className="stat-item">
                        <h3>Total Transcription Audio Processed</h3>
                        <p>{totalAudio}</p>
                      </div>
                      <div className="stat-item">
                        <h3>Number of Owned Rooms</h3>
                        <p>{totalHuddlerooms}</p>
                      </div>
                    </div>
                  )}
                  {activeTab === "robosysml" && (
                    <div className="robosysml-content">
                      <div className="stat-item">
                        <h3>Total Diagrams Generated</h3>
                        <p>{totalDiagrams}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Right Column: Profile & Link Grid */}

        <div className="dashboard-right">
          <div className="profile-header">
            <h1>Your Profile</h1>
            {userPlan === "Free" && (
              <div className="buttons">
                <a
                  className="btn"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePremium();
                  }}
                >
                  <i
                    className="fas fa-crown"
                    style={{ color: "#ffd700", marginRight: "8px" }}
                  ></i>
                  Get Pro
                </a>
              </div>
            )}
          </div>

          <div className="pcard" style={{ position: "relative" }}>
            {isLoading && (
              <div
                className="pcard-loading-overlay"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.22)", // semi-transparent overlay
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                }}
              >
                {/* Spinner markup from Uiverse.io by satyamchaudharydev */}
                <Spinner />
              </div>
            )}

            <div className="image"></div>
            <div className="pcard-info">
              <span>{email}</span>
              <p>{userPlan}</p>
              <p>
                {isVerified ? (
                  "Verified"
                ) : (
                  <span
                    onClick={handleVerifyClick}
                    style={{
                      cursor: "pointer",
                      textDecoration: "none",
                      color: "lightblue",
                    }}
                  >
                    Verify your email
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Link Grid */}
          <h2>Your Tools</h2>
          <div className="notice">
            <p>
              {isVerified
                ? ""
                : "Access to tools is restricted until email is verified"}
            </p>
          </div>
          <div className="hover-tools">
            <div className="tool-grid">
              {!isVerified ? (
                <>
                  <div className="tool-itemn">
                    <span>HuddleRoom</span>
                  </div>
                  <div className="tool-itemn">
                    <span>RoboSysML</span>
                  </div>
                </>
              ) : userPlan === "Free" ? (
                <>
                  <div className="tool-item">
                    <span onClick={handleHuddleRoomClick}>HuddleRoom</span>
                  </div>
                  <div className="tool-item">
                    <span onClick={handleSysMLClick}>RoboSysML</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="tool-item">
                    <span onClick={handleHuddleRoomClick}>HuddleRoom</span>
                  </div>
                  <div className="tool-item">
                    <span onClick={handleSysMLClick}>RoboSysML</span>
                  </div>
                </>
              )}

              <div className="tool-itemn">
                <span>Coming Soon</span>
              </div>
              <div className="tool-itemn">
                <span>Coming Soon</span>
              </div>
              <div className="tool-itemn">
                <span>Coming Soon</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!isVerified && showVerificationModal && (
        <>
          <div className="modal-overlay">
            <div className="verification-modal">
              <h2>Email Verification Required</h2>
              <p>Verify your email to unlock all features.</p>
              <p>Check your inbox or spam for the verification link.</p>

              <button
                onClick={() => {
                  handleVerifyClick();
                  setShowVerificationModal(false);
                }}
              >
                Send Verification Email
              </button>
              <button onClick={() => setShowVerificationModal(false)}>
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
